/**
 * Billing tab — read-only summary of current subscription + last 24 paid
 * invoices. The "Manage subscription" button creates a Stripe Customer
 * Portal session so users can update payment methods or cancel — Ember
 * (free) users get a link to /pricing instead, since they have no
 * subscription to manage yet.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowUpRight, Receipt } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { fmtMoney } from '../../lib/format';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_DISPLAY = { free: 'Ember', pro: 'Steel', pro_plus: 'Damascus', pro_premium: 'Titanium' };

const BillingTab = () => {
  const [data, setData] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/account/billing`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load billing.'));
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await axios.post(
        `${API}/stripe/portal-session`,
        { return_url: window.location.href },
        { withCredentials: true },
      );
      window.location.href = r.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not open the billing portal.';
      toast.error(detail);
      setPortalLoading(false);
    }
  };

  if (!data) return <div className="text-sm text-[#64748B]">Loading…</div>;

  const hasSubscription = !!data.subscription_status && data.tier !== 'free';

  return (
    <div className="space-y-6 max-w-3xl" data-testid="billing-tab">
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="billing-current-card">
        <h2 className="text-xl font-bold text-[#020617] mb-1">Current plan</h2>
        <div className="flex items-baseline gap-2 mt-2">
          <span className="text-3xl font-extrabold text-[#020617]" data-testid="billing-tier-name">
            {TIER_DISPLAY[data.tier] || data.tier}
          </span>
          {data.is_comped && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#FEF3C7] text-[#92400E]" data-testid="billing-comp-pill">
              Comped
            </span>
          )}
        </div>
        <div className="mt-3 text-sm text-[#475569] space-y-1">
          {data.subscription_status && <div>Status: <span className="font-medium text-[#020617]">{data.subscription_status}</span></div>}
          {data.subscription_billing_cycle && <div>Billing cycle: <span className="font-medium text-[#020617]">{data.subscription_billing_cycle}</span></div>}
          {data.subscription_renews_at && (
            <div>{data.subscription_cancel_at_period_end ? 'Ends' : 'Renews'}: <span className="font-medium text-[#020617]">{new Date(data.subscription_renews_at).toLocaleDateString('en-GB')}</span></div>
          )}
          {data.subscription_cancel_at_period_end && (
            <div className="text-amber-700 font-medium">Your subscription is set to cancel at the end of this period.</div>
          )}
          {data.comp_expires_at && (
            <div>Comp expires: <span className="font-medium text-[#020617]">{new Date(data.comp_expires_at).toLocaleDateString('en-GB')}</span></div>
          )}
        </div>
        {hasSubscription ? (
          <Button
            onClick={openPortal}
            disabled={portalLoading}
            className="mt-5 bg-[#0052FF] text-white hover:bg-[#003ECC]"
            data-testid="billing-manage-button"
          >
            {portalLoading ? 'Opening portal…' : 'Manage subscription'}
            <ArrowUpRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            asChild
            className="mt-5 bg-[#0052FF] text-white hover:bg-[#003ECC]"
            data-testid="billing-manage-button"
          >
            <Link to="/pricing">
              See plans
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        )}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="billing-receipts-card">
        <h2 className="text-xl font-bold text-[#020617] mb-1">Receipts</h2>
        <p className="text-sm text-[#64748B] mb-4">Your last 24 paid invoices.</p>

        {(data.receipts || []).length === 0 ? (
          <div className="text-sm text-[#94A3B8] italic">No receipts yet — paid subscriptions will appear here.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[#E2E8F0]">
                <tr>
                  <th className="text-left py-2 text-xs uppercase tracking-wider font-semibold text-[#64748B]">Date</th>
                  <th className="text-left py-2 text-xs uppercase tracking-wider font-semibold text-[#64748B]">Plan</th>
                  <th className="text-left py-2 text-xs uppercase tracking-wider font-semibold text-[#64748B]">Cycle</th>
                  <th className="text-right py-2 text-xs uppercase tracking-wider font-semibold text-[#64748B]">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.receipts.map((r) => (
                  <tr key={r.session_id} className="border-b border-[#E2E8F0] last:border-0" data-testid={`receipt-${r.session_id}`}>
                    <td className="py-2 text-[#334155]">{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : '—'}</td>
                    <td className="py-2 text-[#334155]">
                      <span className="inline-flex items-center gap-1.5">
                        <Receipt className="w-3.5 h-3.5 text-[#94A3B8]" />
                        {TIER_DISPLAY[r.tier] || r.tier}
                      </span>
                    </td>
                    <td className="py-2 text-[#334155] capitalize">{r.billing_cycle}</td>
                    <td className="py-2 text-right font-semibold text-[#020617]">£{fmtMoney(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingTab;
